import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import './ReviewCard.css';

/**
 * ReviewCard Component
 * Displays a customer review with an image in the grid
 * Clicking the card opens the listing on Etsy
 */
const ReviewCard = React.memo(
  ({ listingId, rating, reviewText, imageUrl, createTimestamp, listingUrl }) => {
    const [imageLoaded, setImageLoaded] = useState(false);
    const [imageError, setImageError] = useState(false);

    // Debug: Log that review card is rendering
    console.log('🌟 ReviewCard rendering for listing:', listingId);

    // Handle click to open listing on Etsy
    const handleClick = useCallback(() => {
      if (listingUrl) {
        window.open(listingUrl, '_blank', 'noopener,noreferrer');
      }
    }, [listingUrl]);

    const handleImageLoad = useCallback(() => {
      setImageLoaded(true);
    }, []);

    const handleImageError = useCallback(() => {
      setImageError(true);
    }, []);

    // Format timestamp to readable date
    const formatDate = (timestamp) => {
      if (!timestamp) return '';
      const date = new Date(timestamp * 1000);
      return date.toLocaleDateString('en-US', {
        month: 'short',
        year: 'numeric',
      });
    };

    // Decode HTML entities in text
    const decodeHtmlEntities = (text) => {
      if (!text) return text;
      const textarea = document.createElement('textarea');
      textarea.innerHTML = text;
      return textarea.value;
    };

    // Truncate review text to ~150 characters
    const truncateText = (text, maxLength = 150) => {
      if (!text) return text;
      // Decode HTML entities first
      const decoded = decodeHtmlEntities(text);
      if (decoded.length <= maxLength) return decoded;
      return `${decoded.substring(0, maxLength).trim()}...`;
    };

    return (
      <div
        className="review-card"
        onClick={handleClick}
        style={{ cursor: listingUrl ? 'pointer' : 'default' }}
      >
        {/* Image Section - fills entire card */}
        <div className="review-image-container">
          {!imageLoaded && !imageError && (
            <div className="review-loader-container">
              <div className="review-loader-spinner"></div>
            </div>
          )}

          {imageError && (
            <div className="review-image-error">
              <span style={{ fontSize: '48px', color: '#666' }}>📷</span>
            </div>
          )}

          <img
            src={imageUrl}
            alt="Customer review"
            className={`review-image ${imageLoaded ? 'loaded' : ''}`}
            onLoad={handleImageLoad}
            onError={handleImageError}
            loading="lazy"
          />
        </div>

        {/* Review Content - Overlay on image */}
        <div className="review-content">
          {/* Top: 5 Stars */}
          <div className="review-stars">
            {[...Array(5)].map((_, index) => (
              <span key={index} className="review-star">
                ★
              </span>
            ))}
          </div>

          {/* Bottom: Review Text */}
          {reviewText && <p className="review-text">{truncateText(reviewText)}</p>}
        </div>
      </div>
    );
  }
);

ReviewCard.propTypes = {
  listingId: PropTypes.string.isRequired,
  rating: PropTypes.number.isRequired,
  reviewText: PropTypes.string,
  imageUrl: PropTypes.string.isRequired,
  createTimestamp: PropTypes.number,
  listingUrl: PropTypes.string,
};

ReviewCard.displayName = 'ReviewCard';

export default ReviewCard;
